
import * as React from 'react';
import ConfirmationSelectField from '__common/components/ConfirmationSelectField';
import roofPitchTooltip from './projectConfiguration/fieldsTooltips/roofPitchTooltip';
import { connect } from 'react-redux';
import { RESET_PANELS_STATE, SET_TILTED_ROOF } from 'actions';
import { SET_ROOF_ROOF_PITCH } from '__editor/googleMapsRoofsSelector/googleMapsRoofsSelectorActions';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import CustomTooltip from '__common/components/CustomTooltip';
import { ErrorOutline } from '@mui/icons-material';
import { isFlatRoof, isGableRoof, isHipRoof } from './projectConfiguration/projectConfiguration';
import { isRMIFIProduct, isRMGridFlex, isEcoFoot2Plus, isAscender, isRM5, isRM10orRM10Evo, isRmGridflex10D, isSMTilt, isNxtTilt, isRmGridflex5Rwdi } from '__common/constants/products';
import { isASCE710, isASCE716or722 } from '__common/constants/buildingCodes';
import { AttachmentType } from './projectConfiguration/fields/attachmentType';
import { adjustableTiltSystem, greaterThanEqualToProjectVersion, roofPitchUpdate } from '__common/utils/versionCompare/versionCompare';
import { state } from '__common/store';
import { VERSION_MAP } from '__common/utils/versionCompare/version_info';

type Props = {
  productId: number,
  dispatch: Function;
  panels: panelsState;
  tiltedRoof: tiltedRoofState;
  background: backgroundState,
  projectEnvConfig: projectEnvConfig;
  seismicDesignCategory: string;
  location: string;
  projectVersion: string;
};

class RoofPitchSelector extends React.Component<Props, {}> {
  roofPitches = [
    '---',
    '1/12',
    '2/12',
    '3/12',
    '4/12',
    '5/12',
    '6/12',
    '7/12',
    '8/12',
    '9/12',
    '10/12',
    '11/12',
    '12/12',
  ];

  roofPitchesDegrees = [
    { label: '---', value: '---', id: '---' },
    ...Array.from({ length: 45 }, (_, i) => {
      const degree = i + 1;
      const pitchDegrees: { [key: number]: string } = {
        5: '1/12',
        9: '2/12',
        14: '3/12',
        18: '4/12',
        22: '5/12',
        26: '6/12',
        30: '7/12',
        33: '8/12',
        36: '9/12',
        39: '10/12',
        42: '11/12',
        45: '12/12',
      };
      const pitch = pitchDegrees[degree];
      const label = pitch ? `${degree}° (${pitch})` : `${degree}°`;
      return { label, value: degree.toString(), id: degree.toString() };
    })
  ];

  roofPitchesIFI = (projectVersion)=> { 
    return [
      '---',
      ...(greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['default_roofpitch_value_changes'])? [] : ['0']),
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
    ];};

  roofPitchesIFILimitedForUNIRAC_FLASHLOC_RM = (projectVersion) => {
    return [ // if UNIRAC_FLASHLOC_RM is selected, then only these roof pitches are available for selection in the roof pitch selector dropdown list 
    '---',
    ...(greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['default_roofpitch_value_changes'])? [] : ['0']),
    '1',
    '2',
    '3',
    '4',
    '5',
  ];};

  roofPitchesIFILimited = (projectVersion) => {
    return [
    '---',
    ...(greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['default_roofpitch_value_changes'])? [] : ['0']),
    '1',
    '2',
    '3',
  ];};

  asce716RoofPitch = [
    '---',
    '2/12',
    '3/12',
    '4/12',
    '5/12',
    '6/12',
    '7/12',
    '8/12',
    '9/12',
    '10/12',
    '11/12',
    '12/12',
  ];

  asce716RoofPitchDegrees = [
    { label: '---', value: '---', id: '---' },
    ...Array.from({ length: 37 }, (_, i) => {
      const degree = i + 9;
      const pitchDegrees: { [key: number]: string } = {
        9: '2/12',
        14: '3/12',
        18: '4/12',
        22: '5/12',
        26: '6/12',
        30: '7/12',
        33: '8/12',
        36: '9/12',
        39: '10/12',
        42: '11/12',
        45: '12/12',
      };
      const pitch = pitchDegrees[degree];
      const label = pitch ? `${degree}° (${pitch})` : `${degree}°`;
      return { label, value: degree.toString(), id: degree.toString() };
    })
  ];

  getRoofPitches = () => {
    const { projectEnvConfig: { building_type, building_code, attachment_type, allow_mechanical_attachments }, productId, location, projectVersion } = this.props;

    if (isRMIFIProduct(productId)) {
      let choices = (attachment_type === AttachmentType.UNIRAC_FLASHLOC_RM && allow_mechanical_attachments) ? this.roofPitchesIFILimitedForUNIRAC_FLASHLOC_RM(projectVersion) : this.roofPitchesIFI(projectVersion);

      if (isASCE710(building_code) && location.split(',')[1] && location.split(',')[1].trim() === 'CA') {
        choices = this.roofPitchesIFILimited(projectVersion);
      }
      return choices;
    }

    if((isSMTilt(productId) && adjustableTiltSystem(projectVersion))|| isNxtTilt(productId)) return this.roofPitchesIFI(projectVersion);

    if(isRM5(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) || isRM10orRM10Evo(productId)){
      return (attachment_type === AttachmentType.UNIRAC_FLASHLOC_RM && this.props.projectEnvConfig.allow_mechanical_attachments) ? this.roofPitchesIFILimitedForUNIRAC_FLASHLOC_RM (projectVersion): this.roofPitchesIFI(projectVersion);
    }

    if(isEcoFoot2Plus(productId)) {
      return this.roofPitchesIFILimited(projectVersion);
    }

    if (!isASCE716or722(building_code) || isAscender(productId)) {
      return roofPitchUpdate(projectVersion) ? this.roofPitchesDegrees : this.roofPitches;
    }
    
    if (isFlatRoof(building_type)) {
      return [];
    }

    if (isGableRoof(building_type)) {
      return roofPitchUpdate(projectVersion) ? this.asce716RoofPitchDegrees : this.asce716RoofPitch;
    }

    if (isHipRoof(building_type)) {
      return roofPitchUpdate(projectVersion) ? this.asce716RoofPitchDegrees : this.asce716RoofPitch;
    }
  }

  onRoofPitchChange = (roofPitch: string) => {
    const { dispatch, productId, background: { selectedRoofId } } = this.props;
    !(isRM5(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) || isRM10orRM10Evo(productId)) && dispatch(RESET_PANELS_STATE());
    dispatch(SET_TILTED_ROOF(roofPitch));
    if (isRM5(productId) || isRmGridflex10D(productId) || isRM10orRM10Evo(productId)) {
      dispatch(SET_ROOF_ROOF_PITCH(selectedRoofId, roofPitch));
    }
  }

  warningMessage = (productId) => {
    return (
      <div className="input-warning lack-of-roof-pitch">
        You should set roof {isRMGridFlex(productId) ? 'slope' : 'pitch'}
      </div>
    );
  }

  getLabel = (productId) => {

    if (isRMGridFlex(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) || isEcoFoot2Plus(productId) || isRM5(productId) || isRM10orRM10Evo(productId)) {
      return <div className="input-label">Select roof slope (degrees) </div>;
    }

    return (
      <div className="input-label">
        Select roof pitch
        <CustomTooltip title={roofPitchTooltip()} placement="bottom" isHtml variant="config">
          <ErrorOutline sx={{ fontSize: 16, marginLeft: 0.5, color: '#666', cursor: 'pointer' }} />
        </CustomTooltip>
      </div>
    );
  }

  getTitle = productId => isRMGridFlex(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) || isEcoFoot2Plus(productId) || isRM5(productId) || isRM10orRM10Evo(productId) ? 'Are you sure you want to change the roof slope?' : 'Are you sure you want to change the roof pitch?'

  roofPitchSelect(roofPitches: { label: string, value: string, id: string }[], label) {
    const {
      panels: { panels },
      productId,
    } = this.props;
    const {
      tiltedRoof: { roofPitch },
      background: { bgLoaded },
    } = this.props;
    const {projectConfiguration:{projectEnvConfig:{is_aurora_project, is_os_project}}} = state();
    const isDisable = is_aurora_project || is_os_project;
    return (
      <div className="array-setting-field roof-pitch-field">
        {roofPitch === '---' && this.warningMessage(productId)}
        {label}
        <ConfirmationSelectField
          value={roofPitch}
          items={roofPitches}
          onConfirm={this.onRoofPitchChange}
          title={this.getTitle(productId)}
          content="Current layout will be lost."
          shouldConfirm={!(isRM5(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) || isRM10orRM10Evo(productId)) && panels.length > 0}
          hideSelectedOption
          compactConfirm
          selectFieldProps={{
            fullWidth: true,
            id: isDisable ? `roofPitch-disabled-text` : `roofPitch`,
            name: 'select-field',
            // position: SelectField.Positions.BELOW, // This property has changed in react-md v5
            listZDepth: 0,
            minBottom: 100,
            disabled: !bgLoaded,
            itemLabel: 'label',
            itemValue: 'value',
            MenuProps: {
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: { className: 'roof-pitch-menu-paper' },
              MenuListProps: { className: 'roof-pitch-menu-list' },
            },
          }}
          disabled={isDisable}
        />
      </div>
    );
  }

  render() {
    const { productId } = this.props;

    const roofPitches = (this.getRoofPitches() || []).map(p => {
      if (typeof p === 'string') {
        return { label: p, value: p, id: p };
      }
      return p;
    });

    const label = this.getLabel(productId);

    if (roofPitches.length === 0) {
      return null;
    }

    return this.roofPitchSelect(roofPitches, label);
  }
}

function mapStateToProps(state: appState) {
  return {
    panels: state.panels,
    tiltedRoof: state.tiltedRoof,
    background: state.background,
    projectEnvConfig: state.projectConfiguration.projectEnvConfig,
    seismicDesignCategory: state.projectConfiguration.seismicDesignCategory,
    location: state.projectConfiguration.area,
    projectVersion : state.projectConfiguration.projectVersion,
    productId: state.projectConfiguration.productId,
  };
}

export default connect(mapStateToProps)(RoofPitchSelector);
